import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Row, Col, Alert } from 'react-bootstrap';

export default function UkiesWidget({ componentSettings, setComponentSettings, onChange, selectedComponent, setSelectedComponent, widgets = [] }) {
    const [isInitialized, setIsInitialized] = useState(false);

    // Get current values with defaults
    const selectedWidgetShortcode = componentSettings.widget_shortcode || (widgets.length > 0 ? widgets[0].shortcode : '[widget=1]');
    const selectedWidth = componentSettings.width || 'normal';

    // Parse HTML content to extract widget configuration
    const parseHTMLContent = (htmlContent) => {
        if (!htmlContent) return null;
        
        try {
            // Look for widget shortcode pattern [widget=ID]
            const widgetMatch = htmlContent.match(/\[widget=([^\]]+)\]/);
            
            // Look for width class (structure vs structure-fullwidth)
            const widthMatch = htmlContent.match(/class="([^"]*structure[^"]*)"/);
            let width = 'normal';
            if (widthMatch && widthMatch[1].includes('structure-fullwidth')) {
                width = 'fullwidth';
            }
            
            if (widgetMatch) {
                return {
                    widget_shortcode: widgetMatch[0], // Full shortcode like [widget=1]
                    width: width
                };
            }
            
            return null;
        } catch (error) {
            console.error('Error parsing HTML content:', error);
            return null;
        }
    };

    // Generate HTML content from current form values
    const generateHTMLContent = () => {
        return generateHTMLContentWithSettings(componentSettings);
    };

    // Generate HTML content with specific settings
    const generateHTMLContentWithSettings = (settings) => {
        const widgetShortcode = settings.widget_shortcode || selectedWidgetShortcode;
        const width = settings.width || selectedWidth;
        const widthClass = width === 'fullwidth' ? 'structure-fullwidth' : 'structure';
        
        return `<div class="${widthClass} ukies-widget gap-0">${widgetShortcode}</div>`;
    };

    // Initialize component settings from HTML content on first load
    useEffect(() => {
        if (!isInitialized && selectedComponent?.content) {
            const parsedData = parseHTMLContent(selectedComponent.content);
            if (parsedData) {
                const newSettings = {
                    widget_shortcode: parsedData.widget_shortcode || selectedWidgetShortcode,
                    width: parsedData.width || selectedWidth
                };
                
                setComponentSettings(prev => ({
                    ...prev,
                    ...newSettings
                }));
            }
            setIsInitialized(true);
        } else if (!isInitialized) {
            // No existing content, initialize with defaults
            const newSettings = {
                widget_shortcode: selectedWidgetShortcode,
                width: selectedWidth
            };
            
            setComponentSettings(prev => ({
                ...prev,
                ...newSettings
            }));
            setIsInitialized(true);
        }
    }, [selectedComponent, isInitialized, selectedWidgetShortcode, selectedWidth, setComponentSettings]);

    // Handle widget selection change
    const handleWidgetChange = (shortcode) => {
        const newSettings = {
            ...componentSettings,
            widget_shortcode: shortcode,
            width: 'normal' // Reset width to normal when widget changes
        };
        
        setComponentSettings(newSettings);
        
        // Generate new HTML content and trigger onChange
        const newContent = generateHTMLContentWithSettings(newSettings);
        onChange('content', newContent);
    };

    // Handle width selection change
    const handleWidthChange = (width) => {
        const newSettings = {
            ...componentSettings,
            width: width
        };
        
        setComponentSettings(newSettings);
        
        // Generate new HTML content and trigger onChange
        const newContent = generateHTMLContentWithSettings(newSettings);
        onChange('content', newContent);
    };

    // Get widget name by shortcode for display purposes
    const getWidgetNameByShortcode = (shortcode) => {
        const widget = widgets.find(w => w.shortcode === shortcode);
        return widget ? widget.name : shortcode;
    };

    return (
        <div>
            {/* Widget Selection */}
            <Form.Group className="mb-3">
                <Form.Label>Selecteer Widget</Form.Label>
                <Form.Select
                    value={selectedWidgetShortcode}
                    onChange={(e) => handleWidgetChange(e.target.value)}
                >
                    {widgets.length > 0 ? (
                        widgets.map(widget => (
                            <option key={widget.shortcode} value={widget.shortcode}>
                                {widget.name}
                            </option>
                        ))
                    ) : (
                        <>
                            <option value="[widget=1]">Widget 1</option>
                            <option value="[widget=2]">Widget 2</option>
                            <option value="[widget=3]">Widget 3</option>
                            <option value="[widget=4]">Widget 4</option>
                            <option value="[widget=5]">Widget 5</option>
                        </>
                    )}
                </Form.Select>
                {widgets.length === 0 && (
                    <Form.Text className="text-muted">
                        Widgets worden geladen...
                    </Form.Text>
                )}
            </Form.Group>

            {/* Width Selection */}
            <Form.Group className="mb-3">
                <Form.Label>Breedte</Form.Label>
                <Form.Select
                    value={selectedWidth}
                    onChange={(e) => handleWidthChange(e.target.value)}
                >
                    <option value="normal">Normaal</option>
                    <option value="fullwidth">Volle pagina</option>
                </Form.Select>
                <Form.Text className="text-muted">
                    "Volle pagina" kies je alleen als je een afbeelding op volledige breedte wilt.
                </Form.Text>
            </Form.Group>

            {/* Help Information */}
            <Alert variant="info" className="small" style={{ backgroundColor: '#e6ebea90', border: 'none', color: 'var(--ukies-dark-green)'}}>
                <Alert.Heading className="h6">Wat is een Widget?</Alert.Heading>
                <p className="mb-0">
                    Widgets zijn pre-gedefinieerde componenten die je kunt gebruiken in je website. Ze worden doormiddel van een shortcode in je website geplaatst. De content van een widget kun je hier niet aanpassen.<br/>
                    <br />
                    Widgets bieden ook de mogelijkheid om data uit Eliteswitch te tonen. Bijvoorbeeld producten, blogs en evenementen.
                </p>
            </Alert>
        </div>
    );
} 